import React from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'

import logo from '../../static/technighter-light-header.png'

import SearchModal from './SearchModal'
import HamburgerModal from './HamburgerModal'
import DarkToggle from './DarkToggle'

const Transition = styled.div`
  height: 55px;
  z-index: -1000;

  .active {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  }

  .hidden {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  }
`

const StyledHeader = styled.header`
  width: 100%;
  height: 55px;
  background-color: var(--color-default);
  position: fixed;
  z-index: 100;

  nav {
    height: 100%;
    display: flex;
    justify-content: space-between;
    margin: 0;

    @media only screen and (min-width: 1024px) {
      padding: 0rem 20px;

      #mobile-left {
        display: none;
      }
      #mobile-right {
        display: none;
      }
    }

    @media only screen and (min-width: 1440px) {
      padding: 0rem 35px;
    }

    @media only screen and (max-width: 1023px) {
      #desktop-left {
        display: none;
      }
      #desktop-right {
        display: none;
      }
    }
  }
`

const LeftDiv = styled.div`
  height: 100%;
  display: flex;
  align-items: center;

  a {
    color: var(--color-headerNav);
    font-size: 1rem;
    text-decoration: none;
    padding: 1rem;
    letter-spacing: 0.5px;
    font-family: 'Bungee';

    &:hover {
      color: #b8b8b8;
    }

    @media only screen and (max-width: 1300px) {
      font-size: 0.9rem;
    }
    @media only screen and (max-width: 1150px) {
      padding: 0.75rem;
    }
    @media only screen and (max-width: 1080px) {
      font-size: 0.88rem;
      padding: 0.5rem;
    }
  }
`

const LogoLink = styled(Link)`
  height: 100%;
  margin-left: 0 !important;
`

const LogoImage = styled.img`
  margin: 0;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  max-height: 50px;
`

const RightDiv = styled.div`
  height: 100%;
  display: flex;
  align-items: center;

  a {
    color: var(--color-secondary);
    font-size: 1.3rem;
    text-decoration: none;
    padding: 1rem 1.5rem;
    cursor: pointer;

    &:hover {
      color: var(--color-secondary);
    }

    i.fa-search {
      color: #bdbdbd;

      &:hover {
        color: var(--color-primaryCompliment);
      }
    }

    @media only screen and (max-width: 1300px) {
      font-size: 1.25rem;
      padding: 1rem;
    }
  }
`

export default function Header() {
  const [show, setShow] = React.useState(true)
  const [prevScrollPos, setPrevScrollPos] = React.useState(0)
  const [isSearchModalOpen, setSearchModal] = React.useState(false)
  const [isHamburgerModalOpen, setHamburgerModal] = React.useState(false)

  const handleScroll = () => {
    const currentScrollPos = window.pageYOffset
    let visible = true
    if (currentScrollPos <= 50) {
      visible = true
    } else {
      visible = prevScrollPos > currentScrollPos
    }

    if (show !== visible) {
      setShow(visible)
    }
    setPrevScrollPos(currentScrollPos)
  }

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  })

  const handleBack = () => {
    document.body.classList.remove('modal-open')
    document.body.classList.remove('modal-open-mobile')
    document.getElementById('header').classList.remove('modal-open')
    setSearchModal(false)
    setHamburgerModal(false)
  }

  React.useEffect(() => {
    window.addEventListener('popstate', handleBack)

    return () => window.removeEventListener('popstate', handleBack)
  })

  return (
    <>
      {isSearchModalOpen ? (
        <SearchModal
          exitCallback={() => {
            document.body.classList.remove('modal-open')
            document.body.classList.remove('modal-open-mobile')
            document.getElementById('header').classList.remove('modal-open')
            setSearchModal(false)
            setHamburgerModal(false)
          }}
        />
      ) : null}
      {isHamburgerModalOpen ? (
        <HamburgerModal
          exitCallback={() => {
            document.body.classList.remove('modal-open-mobile')
            document.body.classList.remove('modal-open')
            setHamburgerModal(false)
            setSearchModal(false)
          }}
          openSearchModal={() => {
            setSearchModal(true)
          }}
        />
      ) : null}
      <Transition>
        <StyledHeader id="header" className={show ? 'active' : 'hidden'}>
          <nav>
            <LeftDiv id="desktop-left">
              <LogoLink to="/" aria-label="Home">
                <picture>
                  <source srcSet={logo} type="image/webp" />
                  <LogoImage
                    alt="Technighter logo"
                    src={logo}
                    className="lazyload"
                  />
                </picture>
              </LogoLink>
              <Link to="/computing/" activeStyle={{ color: '#b8b8b8' }}>
                Computing
              </Link>
              <Link to="/gaming/" activeStyle={{ color: '#b8b8b8' }}>
                Gaming
              </Link>
              <Link to="/productivity/" activeStyle={{ color: '#b8b8b8' }}>
                Productivity
              </Link>
              <Link to="/vehicles/" activeStyle={{ color: '#b8b8b8' }}>
                Vehicles
              </Link>
            </LeftDiv>
            <RightDiv id="desktop-right">
              <a
                onClick={() => {
                  document.body.classList.add('modal-open')
                  document.getElementById('header').classList.add('modal-open')
                  setSearchModal(true)
                  document.body.classList.add('modal-open')
                }}
                aria-label="Search"
              >
                <i className="fas fa-search"></i>
              </a>
              <a
                href="https://twitter.com/ElektrikSpark"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <i className="fab fa-twitter"></i>
              </a>
              <a
                href="https://facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook"></i>
              </a>
              <a
                href="https://instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <i className="fab fa-instagram"></i>
              </a>
              <a
                href="https://linkedin.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <i className="fab fa-linkedin"></i>
              </a>
              <a
                href="https://youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Youtube"
              >
                <i className="fab fa-youtube"></i>
              </a>
              <DarkToggle />
            </RightDiv>
            <LeftDiv id="mobile-left">
              <LogoLink to="/" aria-label="Home">
                <picture>
                  <source srcSet={logo} type="image/webp" />
                  <LogoImage
                    alt="Technighter logo"
                    src={logo}
                    className="lazyload"
                  />
                </picture>
              </LogoLink>
            </LeftDiv>
            <RightDiv id="mobile-right">
              <DarkToggle />
              <a
                onClick={() => {
                  document.body.classList.add('modal-open-mobile')
                  setHamburgerModal(true)
                }}
                aria-label="Hamburger"
              >
                <i className="fas fa-bars"></i>
              </a>
            </RightDiv>
          </nav>
        </StyledHeader>
      </Transition>
    </>
  )
}
